import { useStore, NetStateEnum, StateEnum, ValidationError } from '@tt4b/ttam-hooks';
import { computed, ref, Ref, ComputedRef } from '@vue/composition-api';
import { groupBy } from 'lodash-es';
import bcLogo from '@tt4b/ttam-assets/src/images/bc-logo.png';
import { filterKeywords } from './../../../utils/string-utils';
import { isAssetExceedLimit, isShopAdmin, getStoreManagerUrl } from './utils';
import { i18n } from './../../../i18n';
import { ShopType, ShopListItem, AdgroupConfig } from '@tt4b/ttam-enums';
import { StoreBindingTTStatus } from './types';
import { trackEvent } from './../../../utils/index';

/**
 * 获取 Shop 分组名称和 icon
 * 如果资产化以 BC 分组，否则以 adv 分组
 */
export function useShopGroupDisplay() {
  const groupName = computed(() => group => {
    const { state } = useStore();
    const accountInfo = computed(() => state[StateEnum.accountInfo].data.value);
    const advertiserName = computed(() => accountInfo.value?.account?.name ?? '');
    if (group?.length > 0) {
      return group[0].bc_info?.name ?? advertiserName;
    }
    return advertiserName;
  });

  return {
    groupName,
  };
}

/**
 * 获取Shop 分组名称和 icon
 * 以 BC 分组，否则以 adv 分组
 */
const genGroupItem = catalogItem => {
  const groupName = item => {
    const { state } = useStore();
    const advertiserName = state.accountInfo.data.value?.account?.name;
    return item.bc_info?.name ?? advertiserName;
  };

  const groupImage = item => item.bc_info?.picture_url ?? bcLogo;

  return {
    ...catalogItem,
    is_group: true,
    name: groupName(catalogItem),
    picture_url: groupImage(catalogItem),
  };
};

/**
 * 展示的 Shop
 * 过滤并进行按照 bc 分组
 * @param list Shop 列表
 * @returns
 */
export function useShopDisplayList(list) {
  const keywords = ref('');

  const filterList = computed(
    () =>
      list?.value.filter(({ name, id, oec_shop_code }) =>
        filterKeywords(`${name}${id}${oec_shop_code || ''}`, keywords.value),
      ) || [],
  );
  const displayList = computed(() => {
    const arr = [];
    const groupMap = groupBy(filterList.value, item => item.bc_info.id || '0');
    Object.keys(groupMap).forEach(id => {
      const items = groupMap[id];
      if (items[0]) {
        // 插入 group 并将整个数据打平
        const groupItem = genGroupItem(items[0]);
        arr.push(groupItem, ...items);
      }
    });
    return arr;
  });

  return {
    keywords,
    filterList,
    displayList,
  };
}

/**
 * 是否展示提醒
 * @param opts
 * @returns
 */
export function useAlert(opts: { shop: Ref<ShopListItem> }) {
  const visible = computed(() => Boolean(opts.shop?.value?.isNotBindTT));
  const isAdmin = computed(() => isShopAdmin(opts.shop?.value));
  const storeManagerUrl = computed(() => getStoreManagerUrl(opts.shop?.value));
  return {
    visible,
    isAdmin,
    storeManagerUrl,
  };
}

type Operate = () => void;
export function usePropsOperationList(opts: {
  refresh: Operate;
  isEdit: Ref<boolean>;
  adgroupConfig: Ref<AdgroupConfig>;
}) {
  const operationList = computed(() => [
    {
      icon: 'vi-icon2-refresh',
      click: opts.refresh,
      disabled: opts.isEdit.value || opts.adgroupConfig.value.disableAll,
      hint: i18n.t('adgroup_convert-refresh'),
      trackProps: {
        'data-tea-click': 'module_ad_group_refresh_shop_catalog_click',
        'data-tea-expose': 'module_ad_group_refresh_shop_catalog_impression',
        'data-tea-source': 'shop',
      },
    },
  ]);
  return {
    operationList,
  };
}

const getBindingStatus = (shop: ShopListItem) => {
  const { type, tiktok_binding_status_3p } = shop;
  return type === ShopType.tt4bShop && tiktok_binding_status_3p !== StoreBindingTTStatus.binded;
};

const getAssetLimit = (shop: ShopListItem) => {
  const { id, available_quota = 1000 } = shop;
  return isAssetExceedLimit(id, available_quota);
};

const getCountryOfflineStatus = (shop: ShopListItem, areaList) => {
  const validCountry = shop.country?.filter(code => {
    const area = areaList.find(item => item.code === code);
    return !area?.disabled;
  });
  return !validCountry?.length;
};
function formatShopRoiInfo(list) {
  return list.map((shop: ShopListItem) => {
    const roi2MutexInfo = shop?.roi2_mutex_info ?? {
      allow_create_roi1_ad: true,
    };
    const isRoiDisabled = !roi2MutexInfo.allow_create_roi1_ad;
    return {
      ...shop,
      isRoiDisabled,
    };
  });
}
function formatShopList(list) {
  const { state } = useStore();
  const areaList = state[NetStateEnum.areaData].data?.value?.areaList || [];

  return list.map((shop: ShopListItem) => {
    shop.isNotBindTT = getBindingStatus(shop);
    shop.isExceedLimit = getAssetLimit(shop);
    shop.isLocationDisabled = getCountryOfflineStatus(shop, areaList);
    const disabled =
      shop.isCountryNoSupport ||
      shop.isNotBindTT ||
      shop.isExceedLimit ||
      shop.isLocationDisabled ||
      shop.isRoiDisabled;
    return {
      ...shop,
      disabled,
    };
  });
}

function filter3pShopList(list: readonly ShopListItem[]) {
  return list.filter(item => ![ShopType.tt4bShop].includes(item.type));
}

function psaFormatShopList(list: readonly ShopListItem[]) {
  const newList = filter3pShopList(list);
  return newList.map(shop => {
    const isCountryNoSupport = !shop?.is_shop_available_for_psa;
    return { ...shop, isCountryNoSupport };
  });
}

export function useDataSource(
  options: {
    isPSA?: ComputedRef<boolean> | Ref<boolean>;
    isStandardAdsWithShop?: ComputedRef<boolean> | Ref<boolean>;
  } = {},
) {
  const { isPSA = ref(false), isStandardAdsWithShop = ref(false) } = options;

  const { getStore } = useStore();
  const { data, isLoading, isEmpty, runningPromise, run, hasLoaded } = getStore(NetStateEnum.storeList, {
    is_get_psa_available_shop: isPSA.value,
    need_allow_roi1_action: true,
  });

  const shopList = computed(() => {
    let list = data.value || [];
    if (isPSA.value) {
      list = formatShopRoiInfo(psaFormatShopList(list));
    } else if (isStandardAdsWithShop.value) {
      list = formatShopRoiInfo(list);
    }
    return formatShopList(list);
  });

  /** 封装一层，默认把psa参数带上 */
  const getShopList = (params = {}) =>
    run({
      is_get_psa_available_shop: isPSA.value,
      need_allow_roi1_action: true,
      ...params,
    });

  return {
    isLoading,
    isEmpty,
    shopList,
    getShopList,
    runningPromise,
    hasLoaded,
  };
}

export function usePangleCloseLoopCountryList() {
  const { getStore } = useStore();
  const { data } = getStore(NetStateEnum.pangleCloseLoopCountryList);

  const pangleCloseLoopCountryList = computed(() => data.value || []);

  return {
    pangleCloseLoopCountryList,
  };
}

interface IProps {
  enableSelectShop: Ref<boolean>;
  setEnableSelectShop: (val: boolean) => void;
}
// shop的checkbox的状态
const enableSelectShop = ref(false);
export const useShopCheckbox = (): IProps => {
  const setEnableSelectShop = (val: boolean) => {
    trackEvent(val ? 'Module_adgroup_shop_selected' : 'Module_adgroup_shop_unselected');
    enableSelectShop.value = val;
  };
  return {
    enableSelectShop,
    setEnableSelectShop,
  };
};

export function useRules(props) {
  return {
    shopValidator: computed(() => [
      {
        validator: (rule, value, callback) => {
          const isEmpty = !value || value === '0';
          if (!props?.canBeEmpty?.value && isEmpty) {
            return callback(
              new ValidationError({
                messageI18nKey: 'module_adgroup_product_setting_shop_empty_error',
                message: i18n.t('module_adgroup_product_setting_shop_empty_error'),
                moduleName: i18n.t('module_specify_product_source'),
                subModuleName: i18n.t('module_adgroup_product_setting_shop'),
              }),
            );
          }
          return callback();
        },
        trigger: 'custom',
      },
    ]),
  };
}
